import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Download, ArrowLeft } from "lucide-react";
import { API } from "@/lib/api";

export default function ReceiptView() {
  const { fid } = useParams();
  const [params] = useSearchParams();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    const t = params.get("t");
    const authToken = localStorage.getItem("fhp_token");
    const q = t ? `rt=${encodeURIComponent(t)}` : authToken ? `auth=${encodeURIComponent(authToken)}` : "";
    const url = `${API}/finance/receipt/${fid}?${q}`;
    let objUrl;
    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) {
          setState({
            loading: false,
            error:
              res.status === 401
                ? "This receipt link has expired, or you need to be signed in to the admin to view it."
                : "Sorry, that receipt could not be found.",
          });
          return;
        }
        const blob = await res.blob();
        objUrl = URL.createObjectURL(blob);
        setState({ loading: false, url: objUrl, type: blob.type || "" });
      } catch {
        setState({ loading: false, error: "Couldn't load the receipt. Please check your connection and try again." });
      }
    })();
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [fid, params]);

  const isImage = state.type && state.type.startsWith("image/");
  const isPdf = state.type && state.type.includes("pdf");

  return (
    <div className="min-h-screen bg-brand-base text-white flex flex-col" data-testid="receipt-view">
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
        <p className="font-display font-semibold text-sm">The Fish House Project — Receipt</p>
        {state.url && (
          <a href={state.url} download={`receipt-${fid}`} className="btn-ghost px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="receipt-download">
            <Download size={15} /> Download
          </a>
        )}
      </div>

      <div className="flex-1 grid place-items-center p-4">
        {state.loading && <p className="text-brand-textMuted" data-testid="receipt-loading">Loading receipt…</p>}

        {state.error && (
          <div className="text-center max-w-md" data-testid="receipt-error">
            <p className="text-brand-textSec">{state.error}</p>
            <a href="/admin/finance" className="text-brand-cyan text-sm inline-flex items-center gap-1 mt-4">
              <ArrowLeft size={14} /> Go to Finance
            </a>
          </div>
        )}

        {state.url && isImage && (
          <img src={state.url} alt="Receipt" className="max-h-[85vh] max-w-full rounded-lg border border-white/10" data-testid="receipt-image" />
        )}

        {state.url && isPdf && (
          <iframe title="Receipt" src={state.url} className="w-full h-[85vh] rounded-lg border border-white/10 bg-white" data-testid="receipt-pdf" />
        )}

        {state.url && !isImage && !isPdf && (
          <div className="text-center" data-testid="receipt-other">
            <p className="text-brand-textSec mb-4">This receipt can't be previewed here.</p>
            <a href={state.url} download={`receipt-${fid}`} className="btn-primary px-5 py-2.5 text-sm inline-flex items-center gap-2">
              <Download size={15} /> Download receipt
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
